/**
 * branding.ts — per-organization white-label palette (SS-DEC-005).
 *
 * GET  /organizations/:id/branding  — any authenticated org member (drives the theme)
 * PUT  /organizations/:id/branding  — ADMIN + `custom_branding` entitlement (enterprise);
 *                                      throws BrandingEntitlementError on 403 feature gate.
 */

import { apiClient } from './client';

export interface OrgBranding {
  primary?: string;
  secondary?: string;
  backgroundDefault?: string;
  backgroundPaper?: string;
  error?: string;
  warning?: string;
  success?: string;
}

/** Thrown when the org's plan lacks the `custom_branding` feature. */
export class BrandingEntitlementError extends Error {
  constructor(message = 'Custom branding requires the Enterprise plan.') {
    super(message);
    this.name = 'BrandingEntitlementError';
  }
}

/** Fetches an organization's white-label branding palette via GET /organizations/:id/branding. */
export async function getOrgBranding(orgId: string | number): Promise<OrgBranding | null> {
  const res = await apiClient.get(`/organizations/${orgId}/branding`);
  const data = res.data?.data ?? res.data;
  return (data?.branding ?? null) as OrgBranding | null;
}

/** Updates an organization's branding palette via PUT /organizations/:id/branding; throws BrandingEntitlementError on 403. */
export async function updateOrgBranding(
  orgId: string | number,
  branding: OrgBranding,
): Promise<OrgBranding | null> {
  try {
    const res = await apiClient.put(`/organizations/${orgId}/branding`, branding);
    const data = res.data?.data ?? res.data;
    return (data?.branding ?? null) as OrgBranding | null;
  } catch (error: unknown) {
    const e = error as { response?: { status?: number; data?: { error?: unknown } } };
    const msg = typeof e?.response?.data?.error === 'string' ? e.response.data.error : '';
    if (e?.response?.status === 403 && /custom_branding|Feature not available|Plan/i.test(msg)) {
      throw new BrandingEntitlementError();
    }
    throw error;
  }
}
